import { useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { LoginHeader } from '@/components/LoginHeader';
import { PickupHeader } from '@/components/PickupHeader';
import { navigationEvents } from '@/lib/navigation-events';
import { useCart } from '@/contexts/CartContext';

/**
 * AppHeader Props
 * 이벤트 기반으로 리팩토링하여 Cart/Notification 토글은 이벤트로 처리
 */
interface AppHeaderProps {
  // 현재 상태
  activeTab: string;
  showPickupMenu: boolean;
  showTableOrderMenu: boolean;
  isLoggedIn: boolean;
  /** 세션 확인 중 여부 @CODE:ISSUE-150 */
  isCheckingSession: boolean;
  isBlurred: boolean;

  // 헤더 정보
  headerTitle: string;

  /**
   * #541: 익명 순수 메뉴판 모드 (?mode=view).
   * true 면 로그인/계정 진입 헤더(LoginHeader) 대신 매장 타이틀 헤더만 렌더.
   */
  isViewOnly?: boolean;

  // 픽업 헤더 props - 알림 토글은 이벤트로 처리, 장바구니는 Context에서 직접 가져옴
  unreadNotificationCount: number;
  onBackFromPickup: () => void;
  onBackFromTableOrder: () => void;

  // 일반 헤더 props
  onBackToHome: () => void;
}

export function AppHeader({
  activeTab,
  showPickupMenu,
  showTableOrderMenu,
  isLoggedIn,
  isCheckingSession,
  isBlurred,
  headerTitle,
  isViewOnly = false,
  unreadNotificationCount,
  onBackFromPickup,
  onBackFromTableOrder,
  onBackToHome,
}: AppHeaderProps) {
  // 장바구니 아이템 개수를 Context에서 직접 가져옴
  const { cartItemCount } = useCart();
  const blurClass = isBlurred ? 'blur-sm' : '';

  // 이벤트 기반 핸들러
  const handleNotificationClick = useCallback(() => navigationEvents.toggleNotifications(), []);
  const handleCartClick = useCallback(() => navigationEvents.toggleCart(), []);
  const handleLoginClick = useCallback(() => navigationEvents.openLoginPage(), []);

  // 픽업 메뉴 헤더
  if (showPickupMenu) {
    return (
      <div className={`fixed top-0 left-0 right-0 z-50 ${blurClass} transition-all duration-300`}>
        <PickupHeader
          onBack={onBackFromPickup}
          unreadNotificationCount={unreadNotificationCount}
          onNotificationClick={handleNotificationClick}
          cartItemCount={cartItemCount}
          onCartClick={handleCartClick}
        />
      </div>
    );
  }

  // 테이블 주문 메뉴 헤더
  if (showTableOrderMenu) {
    return (
      <div className={`fixed top-0 left-0 right-0 z-50 ${blurClass} transition-all duration-300`}>
        <PickupHeader
          onBack={onBackFromTableOrder}
          unreadNotificationCount={unreadNotificationCount}
          onNotificationClick={handleNotificationClick}
          cartItemCount={cartItemCount}
          onCartClick={handleCartClick}
        />
      </div>
    );
  }

  // 홈 탭 + 세션 확인 중 → 로딩 헤더 (@CODE:ISSUE-153 로딩 인디케이터 표시)
  if (activeTab === 'home' && isCheckingSession) {
    return (
      <div className={`fixed top-0 left-0 right-0 z-50 ${blurClass} transition-all duration-300`}>
        <div className="bg-primary relative w-full h-[50px]">
          <div className="flex items-center justify-center size-full">
            <Loader2 className="w-5 h-5 animate-spin text-white" />
          </div>
        </div>
      </div>
    );
  }

  // #541: 익명 순수 메뉴판 모드 → 로그인/계정 진입 없는 매장 타이틀 헤더.
  // LoginHeader / 기본 헤더 분기보다 우선해 계정 진입점을 제거한다.
  // view 모드는 pickup/table 헤더보다 뒤·LoginHeader보다 앞 — 익명 메뉴판은 로그인/계정 헤더 미노출 (showPickupMenu와 공존 불가)
  if (isViewOnly) {
    return (
      <div className={`fixed top-0 left-0 right-0 z-50 ${blurClass} transition-all duration-300`}>
        <div className="bg-primary relative w-full h-[50px]">
          <div className="flex flex-row items-center relative size-full">
            <div className="relative flex items-center justify-center w-full h-[50px] px-[20px]">
              <p
                className="font-['Noto_Sans:Bold',_'Noto_Sans_KR:Bold',_sans-serif] font-bold text-[16px] text-white tracking-[-0.32px] text-center uppercase"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                {headerTitle}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 홈 탭 + 비로그인 상태 → 로그인 헤더 (@CODE:ISSUE-150 세션 확인 중에는 표시 안함)
  if (activeTab === 'home' && !isLoggedIn && !isCheckingSession) {
    return (
      <div className={`fixed top-0 left-0 right-0 z-50 ${blurClass} transition-all duration-300`}>
        <LoginHeader onLoginClick={handleLoginClick} />
      </div>
    );
  }

  // 기본 헤더
  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${blurClass} transition-all duration-300`}>
      <div className="bg-primary relative w-full h-[50px]">
        <div className="flex flex-row items-center relative size-full">
          <div className="relative flex items-center justify-between w-full h-[50px] px-[20px]">
            {activeTab !== 'home' && (
              <button
                onClick={onBackToHome}
                className="absolute left-[20px] flex items-center justify-center w-[32px] h-[32px] z-10"
              >
                <svg className="w-[12px] h-[21px]" fill="none" preserveAspectRatio="none" viewBox="0 0 12 21">
                  <path
                    d="M11.588 0.41098C11.4583 0.280705 11.3042 0.177346 11.1345 0.106823C10.9649 0.0363008 10.783 0 10.5993 0C10.4156 0 10.2337 0.0363008 10.0641 0.106823C9.89442 0.177346 9.74031 0.280705 9.61059 0.41098L0.326717 9.71087C0.223149 9.8144 0.140982 9.93738 0.0849197 10.0728C0.0288573 10.2082 0 10.3533 0 10.4999C0 10.6464 0.0288573 10.7916 0.0849197 10.9269C0.140982 11.0623 0.223149 11.1853 0.326717 11.2888L9.61059 20.5887C10.158 21.1371 11.0406 21.1371 11.588 20.5887C12.1354 20.0404 12.1354 19.1562 11.588 18.6079L3.49955 10.4943L11.5992 2.38063C12.1354 1.84345 12.1354 0.948157 11.588 0.41098Z"
                    fill="white"
                  />
                </svg>
              </button>
            )}
            <p
              className="font-['Noto_Sans:Bold',_'Noto_Sans_KR:Bold',_sans-serif] font-bold text-[16px] text-white tracking-[-0.32px] text-center absolute left-1/2 -translate-x-1/2 uppercase"
              style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
            >
              {headerTitle}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
